import React from 'react';
import {
  XAxis,
  YAxis,
  VerticalGridLines,
  HorizontalGridLines,
  AreaSeries,
  FlexibleXYPlot,
  MarkSeries,
  LineSeries,
  LineMarkSeries,
  VerticalRectSeries,
} from 'react-vis';
import { Stack, Text, Tooltip } from '@fluentui/react';
import structuredClone from 'core-js/stable/structured-clone';
import ChartDefaults from '../../../shared/ChartDefaults';
import Store from '../../../shared/state/Store';
import GraphUtils from '../../GraphUtils';
import Theme from '../../../shared/Theme';
import Utils from '../../../shared/Utils';
import StackTokens from '../../../shared/StackTokens';
import CytoReducer from '../../ModelPreview/cytoscape/CytoReducer';
import ModellerDefaults from '../../ModellerDefaults';

const xAxisStyle = {
  text: {
    fill: ChartDefaults.ThemeColors.AxisTicks,
    fontSize: '10px',
  },
  line: { stroke: ChartDefaults.ThemeColors.AxisLine },
};

const yAxisStyle = {
  text: {
    fill: ChartDefaults.ThemeColors.AxisTicks,
    fontSize: '10px',
  },
  line: { stroke: ChartDefaults.ThemeColors.AxisLine },
};

export default ({
  data,
  nodeConfig,
  chartRef,
  className,
  networkId,
  renderOffScreen = false,
  chartRefreshKey = 0,
  onChartRendered,
}) => {
  const isMountedRef = React.useRef(true);
  React.useEffect(() => () => {
    isMountedRef.current = false;
  }, []);
  const [, dispatch] = Store.useStore();
  const chartType = GraphUtils.getChartType(nodeConfig);
  const nodeType = nodeConfig.configuration.type;
  const nodeGraphics = nodeConfig.graphics || ModellerDefaults.DefaultGraphics;

  let curveFunctionName = nodeGraphics.chart?.curveFunctionName;
  if (!curveFunctionName) {
    curveFunctionName = (nodeType === 'IntegerInterval') ? ChartDefaults.CurveInteger : ChartDefaults.CurveContinuous;
  }

  const integerOffset = (nodeType === 'IntegerInterval') ? 1 : 0;

  let minX = Number.MAX_VALUE;
  let maxX = -Number.MAX_VALUE;

  let minY = Number.MAX_VALUE;
  let maxY = 0;

  const infiniteIndexMapLower = {};
  const infiniteIndexMapUpper = {};

  const formatData = (dataSet, dataSetIndex) => {
    const formattedPoints = dataSet.map((result, index) => {
      const label = `${result?.label ?? ''}`;
      const isInterval = label.indexOf(' - ') >= 0;
      const rangeSplit = label.split(' - ');
      let lower = parseFloat(rangeSplit[0]);
      let upper = parseFloat(rangeSplit[1]);
      let infiniteLower = false;
      let infiniteUpper = false;

      if (!isInterval) {
        upper = lower;
      }

      if (!Number.isFinite(lower)) {
        lower = -Number.MAX_VALUE;
        infiniteIndexMapLower[dataSetIndex] = infiniteIndexMapLower[dataSetIndex] || [];
        infiniteLower = true;
      } else {
        minX = Math.min(minX, lower);
      }

      if (!Number.isFinite(upper)) {
        upper = Number.MAX_VALUE;
        infiniteIndexMapUpper[dataSetIndex] = infiniteIndexMapUpper[dataSetIndex] || [];
        infiniteUpper = true;
      } else {
        maxX = Math.max(maxX, upper);
      }

      let midPoint;
      if (isInterval) {
        if (index === 0 && dataSet.length > 1) {
          midPoint = lower;
        } else if (index === dataSet.length - 1 && dataSet.length > 1) {
          midPoint = upper;
        } else {
          midPoint = (lower + upper) / 2;
        }
      } else {
        midPoint = lower;
      }

      const range = (upper - lower) + integerOffset;

      const x = midPoint;

      let { value } = result;
      if (!Number.isFinite(value)) {
        value = Number.MAX_VALUE * (value < 0 ? -1 : 1);
      }

      const y = (range > 0) ? value / range : value;

      minY = Math.min(minY, y);
      maxY = Math.max(maxY, y);

      if (chartType === ChartDefaults.ChartTypes.Histogram) {
        const dataPoint = {
          x0: lower,
          x: upper + integerOffset,
          y0: 0,
          y,
          index,
        };
        if (infiniteLower) {
          infiniteIndexMapLower[dataSetIndex].push(dataPoint);
        }
        if (infiniteUpper) {
          infiniteIndexMapUpper[dataSetIndex].push(dataPoint);
        }
        return [dataPoint];
      }
      if (Math.abs(upper - lower) < Number.EPSILON) {
        // Check if lower equals upper (single point)
        // Create vertical line: point at bottom (y=0) and point at top (y=value)
        const points = [
          { x: lower, y: 0 },
          { x: lower, y },
        ];
        if (infiniteLower) {
          infiniteIndexMapLower[dataSetIndex].push(...points);
        }
        return points;
      }
      if (isInterval && dataSet.length === 1) {
        // If it is single interval, create a rectangle
        const pointsLower = [
          { x: lower, y: 0 },
          { x: lower, y },
        ];
        const pointsUpper = [
          { x: upper, y },
          { x: upper, y: 0 },
        ];
        if (infiniteLower) {
          infiniteIndexMapLower[dataSetIndex].push(...pointsLower);
        }
        if (infiniteUpper) {
          infiniteIndexMapUpper[dataSetIndex].push(...pointsUpper);
        }
        return [...pointsLower, ...pointsUpper];
      }

      const point = { x, y };
      if (infiniteLower) {
        infiniteIndexMapLower[dataSetIndex].push(point);
      }
      if (infiniteUpper) {
        infiniteIndexMapUpper[dataSetIndex].push(point);
      }
      return [point];
    }).flat(1);

    return formattedPoints;
  };

  const compileSubChartsData = (nodeResultData) => {
    const subChartsData = [];

    if (!nodeResultData || nodeResultData.length === 0) {
      return subChartsData;
    }

    for (let datasetIndex = 0; datasetIndex < nodeResultData.length; datasetIndex += 1) {
      const dsData = data[datasetIndex];
      const dsId = dsData.dataset;
      const dsKey = GraphUtils.convertDatasetIdToKey(dsId);
      const dsChartData = {
        key: dsKey,
        curve: ChartDefaults.CurveFunctions[curveFunctionName],
        data: formatData(dsData.resultValues, datasetIndex),
        color: ChartDefaults.Colors[datasetIndex],
        opacity: 1 - (datasetIndex / nodeResultData.length),
      };

      if (chartType === ChartDefaults.ChartTypes.ScatterLine) {
        dsChartData.style = { fill: 'none' };
        dsChartData.strokeWidth = '2px';
        dsChartData.size = 3;
      }
      if (chartType === ChartDefaults.ChartTypes.Scatter) {
        dsChartData.size = 3;
      }
      if (chartType === ChartDefaults.ChartTypes.Line) {
        dsChartData.style = { fill: 'none' };
        dsChartData.strokeWidth = '2px';
        dsChartData.opacity = 1;
      }
      if (chartType === ChartDefaults.ChartTypes.Histogram) {
        dsChartData.stroke = Theme.Colors.Blackish;
      }

      subChartsData.push(dsChartData);
    }

    return subChartsData;
  };

  const rawData = structuredClone(data);

  const subChartsData = compileSubChartsData(rawData);

  // Check if we have single point cases across all datasets and calculate centered X domain
  const rangeX = Math.abs(maxX - minX);
  const paddingX = (rangeX > 0) ? rangeX * 0.2 : Math.abs(minX) * 0.2;
  let xDomainMin = minX - paddingX;
  let xDomainMax = maxX + paddingX;

  const infinitePadding = paddingX * ChartDefaults.InfinitePaddingFactor;

  if (infiniteIndexMapLower && Object.keys(infiniteIndexMapLower).length > 0) {
    xDomainMin -= paddingX;
    Object.keys(infiniteIndexMapLower).forEach((dsIndex) => {
      infiniteIndexMapLower[dsIndex].forEach((point) => {
        // eslint-disable-next-line no-param-reassign
        if (point.x0 !== undefined) {
          // eslint-disable-next-line no-param-reassign
          point.x0 = xDomainMin;
        } else {
          // eslint-disable-next-line no-param-reassign
          point.x = xDomainMin;
        }
      });
    });
    xDomainMin -= infinitePadding;
  }
  if (infiniteIndexMapUpper && Object.keys(infiniteIndexMapUpper).length > 0) {
    xDomainMax += paddingX;
    Object.keys(infiniteIndexMapUpper).forEach((dsIndex) => {
      infiniteIndexMapUpper[dsIndex].forEach((point) => {
        // eslint-disable-next-line no-param-reassign
        point.x = xDomainMax;
      });
    });
    xDomainMax += infinitePadding;
  }

  if (!Number.isFinite(xDomainMin)) {
    xDomainMin = -Number.MAX_VALUE;
  }
  if (!Number.isFinite(xDomainMax)) {
    xDomainMax = Number.MAX_VALUE;
  }
  const xDomain = [xDomainMin, xDomainMax];

  const edgeTolerance = (xDomainMax - xDomainMin) * 0.01;
  const xAxisTickFormat = (value) => {
    if (Object.keys(infiniteIndexMapLower).length > 0 && Math.abs(value - xDomainMin) <= edgeTolerance) return '-∞';
    if (Object.keys(infiniteIndexMapUpper).length > 0 && Math.abs(value - xDomainMax) <= edgeTolerance) return '∞';

    if (value === 0) return '0';

    const abs = Math.abs(value);

    // Switch to scientific notation outside readable range
    if (abs >= 1e6 || abs < 1e-6) {
      return value.toExponential(2) // 2 decimal places
        .replace('e+', 'e') // prettier
        .replace('e0', ''); // avoid e0
    }

    // Otherwise use compact formatting
    return value.toLocaleString(undefined, {
      maximumFractionDigits: 6,
    });
  };

  let yDomainMax = maxY;
  if (!Number.isFinite(yDomainMax)) {
    yDomainMax = Number.MAX_VALUE;
  }
  const yDomain = [0, yDomainMax];

  let chartComponent;
  switch (chartType) {
    case ChartDefaults.ChartTypes.Area: chartComponent = <AreaSeries />; break;
    case ChartDefaults.ChartTypes.Histogram: chartComponent = <VerticalRectSeries />; break;
    case ChartDefaults.ChartTypes.Scatter: chartComponent = <MarkSeries />; break;
    case ChartDefaults.ChartTypes.Line: chartComponent = <LineSeries />; break;
    case ChartDefaults.ChartTypes.ScatterLine: chartComponent = <LineMarkSeries />; break;
    default: chartComponent = <></>;
  }

  const [tooltipData, setTooltipData] = React.useState(null);
  const [mousePoint, setMousePoint] = React.useState({ x: 0, y: 0 });
  const [mousePointHit/* , setMousePointHit */] = React.useState({ x: 0, y: 0 });
  React.useEffect(() => {
    if (tooltipData === null) {
      return;
    }
    if (Math.abs(mousePointHit.x - mousePoint.x) > 50 || Math.abs(mousePointHit.y - mousePoint.y) > 100) {
      setTooltipData(null);
    }
  }, [mousePoint]);

  // const linesToPlot = [];
  // const plotExtraItemsRequired = nodeChartGraphics.plotPercentiles || nodeChartGraphics.plotMean || nodeChartGraphics.plotMedian;

  // if (plotExtraItemsRequired) {
  //   [
  //     DataReducer.getCachedResultsForDataSet(globalState, DataReducer.DefaultCaseNames.BASELINE),
  //     DataReducer.getCachedResultsForDataSet(globalState, DataReducer.DefaultCaseNames.USER),
  //   ].forEach((dsCalculationData, dsIndex) => {
  //     if (!dsCalculationData) {
  //       return;
  //     }

  //     const key = Utils.createNetworkNodeKey(config.network, config.node);

  //     const resultData = dsCalculationData[key];

  //     if (!resultData) {
  //       return;
  //     }

  //     let dsKey;
  //     let dsColor;
  //     if (dsIndex === 0) {
  //       dsKey = 'baseline';
  //       dsColor = generalConfig.colorBaseline || ChartDefaults.ColorBaseline;
  //     } else {
  //       dsKey = 'user';
  //       dsColor = generalConfig.colorUser || ChartDefaults.ColorUser;
  //     }

  //     const generatePlotData = (uKey, dValue, prefixText) => ({
  //       key: uKey,
  //       color: dsColor,
  //       strokeWidth: '1px',
  //       style: {
  //         filter: 'drop-shadow(#e4e4e4 1px 0px 1px )',
  //       },
  //       data: [
  //         { x: dValue, y: 0 },
  //         { x: dValue, y: maxY },
  //       ],
  //       onSeriesMouseOver: () => {
  //         setTooltipData(null);
  //         setTimeout(() => {
  //           setMousePointHit(mousePoint);
  //           setTooltipData({
  //             color: dsColor,
  //             dsKey: (dsIndex === 0) ? DataReducer.DefaultCaseNames.BASELINE : DataReducer.DefaultCaseNames.USER,
  //             text: prefixText,
  //             value: Utils.roundFormat(dValue, config.decimalPlaces, ChartDefaults.decimalPlaces),
  //           });
  //         }, 1);
  //       },
  //     });

  //     if (config.plotPercentiles) {
  //       if (config.customPercentiles && config.customPercentiles.length > 0) {
  //         if (resultData.percentiles) {
  //           Object.values(config.customPercentiles).forEach((pName) => {
  //             if (!resultData.percentiles[pName]) {
  //               return;
  //             }
  //             linesToPlot.push(generatePlotData(dsKey + pName, resultData.percentiles[pName], `${pName}th percentile`));
  //           });
  //         }
  //       } else if (resultData.summaryStatistics) {
  //         linesToPlot.push(generatePlotData(`${dsKey}lowerPercentile`, resultData.summaryStatistics.lowerPercentile, 'Lower percentile'));
  //         linesToPlot.push(generatePlotData(`${dsKey}upperPercentile`, resultData.summaryStatistics.upperPercentile, 'Upper percentile'));
  //       }
  //     }

  //     if (config.plotMean && resultData.summaryStatistics.mean) {
  //       linesToPlot.push(generatePlotData(`${dsKey}mean`, resultData.summaryStatistics.mean, 'Mean'));
  //     }

  //     if (config.plotMedian && resultData.summaryStatistics.median) {
  //       linesToPlot.push(generatePlotData(`${dsKey}median`, resultData.summaryStatistics.median, 'Median'));
  //     }
  //   });
  // }

  const margins = {};
  ['Left', 'Right', 'Top', 'Bottom'].forEach((p) => {
    const paddingKey = `padding${p}`;
    if (nodeGraphics.chart?.[paddingKey]) {
      margins[p.toLowerCase()] = parseInt(nodeGraphics.chart?.[paddingKey], 10);
    }
    // const marginKey2 = `margin${p}`;
    // if (config[marginKey2]) {
    //   margins[p.toLowerCase()] = parseInt(config[marginKey2], 10);
    // }
  });

  const [paddingBottomAuto, setPaddingBottomAuto] = React.useState(0);
  const [paddingLeftAuto, setPaddingLeftAuto] = React.useState(0);

  const adjustMargins = (svg) => {
    if (!isMountedRef.current) {
      return;
    }
    if (!svg) {
      return;
    }

    const xAxis = svg.getElementsByClassName('rv-xy-plot__axis--horizontal')[0];
    const yAxis = svg.getElementsByClassName('rv-xy-plot__axis--vertical')[0];

    if (!xAxis || !yAxis) {
      return;
    }

    const xAxisLeftBeyondYAxis = yAxis.getBoundingClientRect().x - xAxis.getBoundingClientRect().x;
    setPaddingLeftAuto(Math.max(xAxisLeftBeyondYAxis, 0) + yAxis.getBoundingClientRect().width);

    if (xAxis) {
      const axisRect = xAxis.getBoundingClientRect();
      if (!renderOffScreen) {
        setPaddingBottomAuto(axisRect.height);
      } else {
        let maxLabelWidth = 0;
        let maxRotatedLabelBoxWidth = 0;
        let maxRotatedLabelBoxHeight = 0;
        const tickTexts = xAxis.getElementsByClassName('rv-xy-plot__axis__tick__text');
        if (tickTexts && tickTexts.length > 0) {
          [].forEach.call(tickTexts, (tickText) => {
            const label = tickText?.textContent ?? '';
            const labelMetrics = Utils.estimateTextBox(
              label,
              ChartDefaults.ContinuousChartStyleOptions.fontSize,
              ChartDefaults.ContinuousChartStyleOptions.fontFamily,
              ChartDefaults.ContinuousChartStyleOptions.fontWeight,
            );
            maxLabelWidth = Math.max(maxLabelWidth, labelMetrics);
            const transform = tickText?.getAttribute?.('transform') || '';
            const match = /rotate\(([-\d.]+)/.exec(transform);
            const angleDeg = match ? parseFloat(match[1]) : 0;
            const angleRad = (angleDeg * Math.PI) / 180;
            const labelHeight = (labelMetrics?.actualBoundingBoxAscent ?? 0)
              + (labelMetrics?.actualBoundingBoxDescent ?? 0)
              || parseFloat(ChartDefaults.ContinuousChartStyleOptions.fontSize)
              || 0;
            const rotatedWidth = Math.abs(labelMetrics.width * Math.cos(angleRad))
              + Math.abs(labelHeight * Math.sin(angleRad));
            const rotatedHeight = Math.abs(labelMetrics.width * Math.sin(angleRad))
              + Math.abs(labelHeight * Math.cos(angleRad));
            maxRotatedLabelBoxWidth = Math.max(maxRotatedLabelBoxWidth, rotatedWidth);
            maxRotatedLabelBoxHeight = Math.max(maxRotatedLabelBoxHeight, rotatedHeight);
          });
        }


        setPaddingBottomAuto(40 + maxRotatedLabelBoxHeight);
      }
    }
  };

  React.useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (!isMountedRef.current) {
        return;
      }
      if (data && data.length > 0) {
        const svg = Utils.getSvg(chartRef);
        if (svg) {
          adjustMargins(svg);
        }
      }
    }, 10);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [data[0]?.refreshedAt, nodeConfig.graphics?.chart]);

  React.useEffect(() => {
    if (!renderOffScreen) {
      return undefined;
    }
    let cancelled = false;
    let attemptCount = 0;
    const maxAttempts = 10;
    const attemptDelayMs = 50;

    const attemptUpdate = () => {
      if (cancelled || !isMountedRef.current) {
        return;
      }
      const svg = Utils.getSvg(chartRef);
      if (svg) {
        try {
          dispatch(CytoReducer.updateNodeChart(chartRef, networkId, nodeConfig.id));
          if (typeof onChartRendered === 'function') {
            onChartRendered(networkId, nodeConfig.id, chartRefreshKey);
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('[ContinuousChart] updateNodeChart: Error dispatching updateNodeChart', {
            networkId,
            nodeId: nodeConfig.id,
            error: error.message,
            stack: error.stack,
          });
        }
        return;
      }

      attemptCount += 1;
      if (attemptCount >= maxAttempts) {
        return;
      }

      setTimeout(attemptUpdate, attemptDelayMs);
    };

    const timeoutId = setTimeout(attemptUpdate, attemptDelayMs);
    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [chartRef, data[0]?.refreshedAt, nodeConfig.graphics?.chart]);

  // For offscreen rendering, always use calculated padding (ignore existing padding)
  // For normal rendering, use existing padding if configured, otherwise use calculated
  if (renderOffScreen) {
    // Use calculated padding to ensure chart fits properly
    margins.bottom = paddingBottomAuto;
  } else {
    margins.bottom = margins.bottom !== undefined ? margins.bottom : paddingBottomAuto;
  }
  margins.left = margins.left !== undefined ? margins.left : paddingLeftAuto;

  if (renderOffScreen) {
    margins.left = (margins.left || 0) + ChartDefaults.DefaultHorizontalPadding;
    margins.right = (margins.right || 0) + ChartDefaults.DefaultHorizontalPadding;
  }

  // eslint-disable-next-line no-unused-vars
  const [forceRender, setForceRender] = React.useState(false);
  React.useEffect(() => {
    if (!nodeGraphics.graph?.height) {
      setForceRender(true);
      const timeoutId = setTimeout(() => {
        if (isMountedRef.current) {
          setForceRender(false);
        }
      }, 10);
      return () => {
        clearTimeout(timeoutId);
      };
    }
    setForceRender(false);
    return undefined;
  }, [nodeGraphics.graph?.height]);

  return (
    <div
      style={{
        width: '100%',
        // Use 100% height to fill the container, which may be adjusted for header offset
        height: '100%',
        minHeight: (nodeGraphics.graph?.height === undefined) ? `${ModellerDefaults.DefaultGraphics.graph.height}px` : 'auto',
      }}
      ref={chartRef}
      className={className}
    >
      <FlexibleXYPlot
        margin={margins}
        xDomain={xDomain}
        yDomain={yDomain}
        style={{
          overflow: 'hidden',
          backgroundColor: 'white',
          fontFamily: ChartDefaults.ContinuousChartStyleOptions.fontFamily,
        }}
        onMouseMove={(event) => {
          setMousePoint({ x: event.clientX, y: event.clientY });
        }}
      >
        <VerticalGridLines />
        <HorizontalGridLines />
        <XAxis
          style={xAxisStyle}
          tickLabelAngle={-45}
          tickSizeOuter={5}
          tickSizeInner={0}
          tickFormat={xAxisTickFormat}
        />
        <YAxis
          style={yAxisStyle}
          tickSizeOuter={5}
          tickSizeInner={0}
        />

        {
          subChartsData.map((subChartProps) => (
            React.cloneElement(chartComponent, { ...subChartProps })
          ))
        }

        {/* {linesToPlot.map((pData) => (
          <LineSeries
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...pData}
          />
        ))} */}
      </FlexibleXYPlot>

      {tooltipData !== null && (
        <Tooltip
          x={mousePoint.x}
          y={mousePoint.y}
        >
          <Stack horizontal tokens={StackTokens.spacing}>
            <div
              style={{
                width: '20px',
                height: '20px',
                background: tooltipData.color,
              }}
            />
            <Text>{tooltipData.dsKey}</Text>
            <Text>{`${tooltipData.text}: ${tooltipData.value}`}</Text>
          </Stack>
        </Tooltip>
      )}

    </div>
  );
};
